import { FC, useMemo } from 'react';

import { cva } from 'class-variance-authority';
import { AiOutlineUser } from 'react-icons/ai';
import { HiBuildingOffice } from 'react-icons/hi2';
import { AVATAR_COLORS, SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';

export type PROPS = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  // TODO: add User interface
  user?: any;
  company?: boolean;
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  size?: SIZE;
  text?: string;
  index?: number;
};

const avatarClassnames = cva(
  [
    'rounded-full',
    'flex items-center justify-center',
    'text-fill-tertiary',
    'overflow-hidden',
  ],
  {
    variants: {
      size: {
        [SIZE.Small]: ['text-xs h-6 w-6'],
        [SIZE.Medium]: ['text-[16px] h-11 w-11'],
        [SIZE.Large]: ['text-xl h-[52px] w-[52px]'],
      },
    },
    defaultVariants: { size: SIZE.Medium },
  },
);

const overlayClassnames = cva(
  [
    'rounded-full',
    'bg-[#718FA5] text-fill-tertiary',
    'absolute top-0 left-0',
    'opacity-60',
  ],
  {
    variants: {
      size: {
        [SIZE.Small]: ['h-6 w-6'],
        [SIZE.Medium]: ['h-11 w-11'],
        [SIZE.Large]: ['h-[52px] w-[52px]'],
      },
    },
    defaultVariants: { size: SIZE.Medium },
  },
);

const Avatar: FC<PROPS> = ({
  user = {},
  company,
  className,
  disabled,
  isLoading,
  size,
  text,
  index,
  ...props
}) => {
  const usr = useMemo(() => {
    const { fullName, firstName, lastName, photoUrl, emailAddress } =
      user ?? {};

    if (user === 'AI') {
      return 'AI';
    }

    if (text) {
      return text;
    }

    if (company) {
      return <HiBuildingOffice />;
    }

    if (photoUrl) {
      return <img src={photoUrl} alt={photoUrl} />;
    }

    if (fullName || lastName || firstName) {
      let str = '';
      if (fullName) str = fullName;
      if (lastName) str = lastName;
      if (firstName) str = firstName;
      return str[0]?.toUpperCase();
    }

    if (emailAddress) {
      const eml = emailAddress || '';
      return eml[0]?.toUpperCase();
    }

    return <AiOutlineUser />;
  }, [user, text, company]);

  if (isLoading) return <div className={cn(['mx-auto'], className)} />;

  return (
    <div className="relative">
      <button
        {...props}
        type="button"
        className={cn(
          avatarClassnames({ size }),
          AVATAR_COLORS[index ?? 0],
          className,
        )}
        disabled={disabled}
      >
        {usr}
      </button>
      {disabled && <div className={cn(overlayClassnames({ size }))} />}
    </div>
  );
};

export default Avatar;
